/*
 * Title Case for names, on the browser side.
 *
 * "dilip sheet" and "DILIP SHEET" both become "Dilip Sheet".
 *
 * This is a deliberate mirror of `utils/nameCase.js` in the API
 * (jaminy_jobportal_nodejs_apiai). The two MUST stay in step: if the browser
 * tidies a name and the server does not, or the two disagree about a rule, the
 * value a user sees in the form is not the value that gets stored. Change one,
 * change the other.
 *
 * Applied in TWO stages, and both are needed:
 *
 *   titleCaseOnChange  as the user types. Case only, never length, with the
 *                      caret put back afterwards.
 *   titleCaseOnBlur    when the field is left. Trims, collapses runs of
 *                      spaces, applies the small-word rule for organisations.
 *
 * Doing the trim while typing would delete the space the moment it is pressed,
 * so "dilip " could never become "dilip sheet". Doing nothing until blur means
 * the value silently changes after the user has stopped looking at it. Hence
 * the split.
 *
 * NOT for every text field. Applied to the wrong one it destroys data, because
 * this is an education portal: PGT, TGT, PRT, B.Ed, M.Ed, CTET, DPS, KV, GSSS
 * all become Pgt, B.ed, Dps. Use it on person names, and on an organisation
 * name only through `toOrgTitleCase`, which refuses anything acronym-shaped.
 */

/*
 * A short, entirely capitalised word is left as it is: "RP" is far likelier to
 * be someone's initials than a mis-typed "Rp". Three characters covers initials
 * and the common school acronyms (KV, DPS, DAV) without swallowing a real short
 * name like "Ravi".
 */
const KEEP_AS_IS_MAX = 3;
const isShortAllCaps = (word) =>
    word.length <= KEEP_AS_IS_MAX && word === word.toUpperCase() && /[A-Z]/.test(word);

/*
 * Capitalises one word.
 *
 * The apostrophe is NOT a word boundary — "teacher's job" must become
 * "Teacher's Job", never "Teacher'S Job". The hyphen IS one, so "anne-marie"
 * becomes "Anne-Marie". The first LETTER is capitalised rather than the first
 * character, so "(sagar)" becomes "(Sagar)" instead of keeping the bracket and
 * leaving the word lowercase.
 */
const capitaliseWord = (word) => {
    if (!word) return word;
    if (isShortAllCaps(word)) return word;
    return word
        .split('-')
        .map((part) => part.replace(
            /^([^a-zA-Z]*)([a-zA-Z])(.*)$/,
            (_, lead, first, rest) => lead + first.toUpperCase() + rest.toLowerCase(),
        ))
        .join('-');
};

/*
 * Words that stay lowercase in the middle of the name of a THING — the
 * difference between "Islamic University of Science and Technology" and the
 * "Islamic University Of Science And Technology" a naive pass produces.
 * Organisation names only: "De" in "Ann De Souza" is part of a surname.
 */
const SMALL_WORDS = new Set([
    'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from', 'in', 'into',
    'nor', 'of', 'on', 'onto', 'or', 'over', 'the', 'to', 'up', 'via', 'with',
]);

/*
 * camelCase is an identifier, not something a person typed as their name:
 * "RegisterUser15930", "JobSeeker", "testUser". Title case would render those
 * "Registeruser15930" and "Jobseeker", losing the only thing that made them
 * readable. Detected as an interior capital with no space anywhere — a real
 * two-word name always has the space.
 */
const looksLikeIdentifier = (value) => !/\s/.test(value) && /[a-z][A-Z]/.test(value);

const ACRONYM_RUN = /[A-Z]{2,}/;
/*
 * The same acronym written with full stops: D.A.V., G.D., K.B.D., J.K.G.
 * ACRONYM_RUN cannot see these — the dots break the run of capitals — so they
 * passed the guard and came out as "D.a.v." and "K.b.d.", which is worse than
 * the input. Indian school names are full of this shape. Kept identical to the
 * API's utils/nameCase.js, which must agree with this file.
 */
const DOTTED_INITIALS = /(?:[A-Za-z]\.){2,}/;
const MC_MAC = /(^|[\s-])(Mc|Mac)[A-Z]/;
const CAP_AFTER_APOSTROPHE = /'[A-Z]/;

/**
 * Title-cases a PERSON's name. Trims and collapses runs of whitespace.
 * Returns the input untouched when there is nothing sensible to do with it.
 *
 * @param {string} raw
 * @param {{ smallWords?: boolean }} [opts]
 * @returns {string}
 */
export const toTitleCase = (raw, opts = {}) => {
    if (raw === null || raw === undefined) return raw;
    const value = String(raw).trim().replace(/\s+/g, ' ');
    if (value === '') return value;
    // Nothing alphabetic — a phone number typed into a name box, say. Returned
    // as-is rather than "fixed", so the bad entry stays visible to the user.
    if (!/[a-zA-Z]/.test(value)) return value;
    if (looksLikeIdentifier(value)) return value;

    const words = value.split(' ');
    return words
        .map((word, i) => {
            // First and last word are always capitalised, however small:
            // "Ministry of" and "the Ridge School" would both read wrongly.
            const isEdge = i === 0 || i === words.length - 1;
            if (opts.smallWords && !isEdge && SMALL_WORDS.has(word.toLowerCase())) {
                return word.toLowerCase();
            }
            return capitaliseWord(word);
        })
        .join(' ');
};

/**
 * Title-cases an ORGANISATION name — a school, institution or company.
 *
 * Refuses anything carrying capitalisation a rule cannot reproduce, because
 * getting these wrong is worse than leaving them alone:
 *
 *   "DPS", "GSSS Dhar", "RD Tech", "SS Vidhya Mandir"  -> untouched
 *   "McDonald", "St Xavier'S"                          -> untouched
 *   "baba nebhraj sr sec school"  -> "Baba Nebhraj Sr Sec School"
 *   "techfour technology "        -> "Techfour Technology"
 *
 * @param {string} raw
 * @returns {string}
 */
export const toOrgTitleCase = (raw) => {
    if (raw === null || raw === undefined) return raw;
    const value = String(raw).trim();
    if (value === '' || !/[a-zA-Z]/.test(value)) return raw;
    if (ACRONYM_RUN.test(value) || DOTTED_INITIALS.test(value)
        || MC_MAC.test(value) || CAP_AFTER_APOSTROPHE.test(value)) {
        // Only tidy the whitespace; the casing is left exactly as entered.
        return value.replace(/\s+/g, ' ');
    }
    return toTitleCase(value, { smallWords: true });
};

/**
 * The AS-YOU-TYPE variant. Changes case ONLY — never length.
 *
 * This is the whole reason it exists separately from `toTitleCase`. That one
 * trims and collapses whitespace, which is right when a field is finished with
 * and catastrophic on every keystroke: the space you press to begin a second
 * word is removed the instant you press it, so "dilip " can never grow into
 * "dilip sheet". Here every space, including trailing and doubled ones, is left
 * exactly as typed and tidied later on blur.
 *
 * Because the output is always the same length as the input, a caret index into
 * the old string is still valid for the new one — which is what makes
 * `titleCaseOnChange` able to put the cursor back.
 */
export const toTitleCaseLive = (raw, { smallWords = false } = {}) => {
    if (raw === null || raw === undefined) return raw;
    const value = String(raw);
    if (value === '' || !/[a-zA-Z]/.test(value)) return value;
    if (looksLikeIdentifier(value.trim())) return value;

    // Split on spaces but KEEP them, so the joined result is byte-for-byte the
    // same length as the input.
    const parts = value.split(/( +)/);
    const wordIndexes = parts.reduce((acc, p, i) => (/^ *$/.test(p) ? acc : acc.concat(i)), []);
    const firstWord = wordIndexes[0];
    const lastWord = wordIndexes[wordIndexes.length - 1];

    return parts
        .map((part, i) => {
            if (/^ *$/.test(part)) return part;
            const isEdge = i === firstWord || i === lastWord;
            if (smallWords && !isEdge && SMALL_WORDS.has(part.toLowerCase())) {
                return part.toLowerCase();
            }
            return capitaliseWord(part);
        })
        .join('');
};

/**
 * Change handler that title-cases while the user types, and puts the caret back
 * where it was.
 *
 *   onChange={titleCaseOnChange(setName)}
 *
 * Without the caret restore this is unusable for editing: React re-assigns the
 * input's `value` whenever the transform differs from what was typed, and the
 * browser answers that by dropping the cursor at the end of the field. Typing
 * at the end looks fine; correcting a letter in the middle of a name does not.
 *
 * `sanitise` runs first, for a field that also restricts which characters are
 * allowed (Register's Full Name strips anything outside letters and spaces).
 */
export const titleCaseOnChange = (setter, { org = false, sanitise } = {}) => (event) => {
    const el = event.target;
    const typed = el.value ?? '';
    const cleaned = sanitise ? sanitise(typed) : typed;
    const next = toTitleCaseLive(cleaned, { smallWords: org });

    if (next !== typed) {
        /*
         * Written to the DOM and the caret fixed SYNCHRONOUSLY, inside the
         * event.
         *
         * The first version deferred this to requestAnimationFrame, which
         * scrambled anything typed at speed: the callback ran after later
         * keystrokes had already been handled, dragged the cursor back to a
         * stale index, and the next character landed in the wrong place —
         * "DILIP SHEET" came out as "Dilip Seeht". Doing it here leaves no
         * window for that. React's own re-render then assigns the identical
         * string, which does not move the selection.
         */
        const caret = el.selectionStart;
        // A sanitiser can drop characters, so the caret shifts left by however
        // many disappeared before it.
        const removed = typed.length - next.length;
        el.value = next;
        if (caret !== null && caret !== undefined) {
            const pos = Math.max(0, Math.min(next.length, caret - removed));
            try { el.setSelectionRange(pos, pos); } catch (e) { /* not a text input */ }
        }
    }

    setter(next);
};

/**
 * Blur handler — the final tidy: trims, collapses runs of spaces, and applies
 * the small-word rule for organisation names. Pairs with `titleCaseOnChange`,
 * which deliberately leaves whitespace alone while typing.
 *
 *   onBlur={titleCaseOnBlur((v) => setName(v))}
 *
 * Calls back only when the value actually changes, so it cannot cause a render
 * loop or mark a pristine form dirty.
 */
export const titleCaseOnBlur = (setter, { org = false } = {}) => (event) => {
    const raw = event?.target?.value ?? '';
    const next = org ? toOrgTitleCase(raw) : toTitleCase(raw);
    if (next !== raw) setter(next);
};

const nameCase = { toTitleCase, toOrgTitleCase, toTitleCaseLive, titleCaseOnChange, titleCaseOnBlur };
export default nameCase;
